// ─── Magazine Editorial Section ─────────────────────────────────────────────
// Large feature article + 3 secondary cards in a luxury editorial layout.
// Reads from DB (magazine_posts), falls back to static POSTS if DB is empty.
// `light` renders the section on a white background (dark text) instead of the
// default near-black; used by the homepage "The Edit" band.
import { useState, useEffect } from 'react';
import { POSTS } from '../../pages/Magazine/data/posts';
import { fetchPosts } from '../../pages/Magazine/../../services/magazineService';
import { orderBy, byDateDesc } from '../../utils/stableSelect';

const FD = "'Gilda Display', 'Playfair Display', Georgia, serif";
const FU = "'Nunito', 'Inter', 'Helvetica Neue', sans-serif";
const GOLD = '#c9a96e';

// Palettes, dark (default) vs light (white background).
const DARK = {
  bg: '#09090a', ink: '#f5f0e8', body: 'rgba(245,240,232,0.58)',
  meta: 'rgba(245,240,232,0.55)', faint: 'rgba(245,240,232,0.4)',
  gold: GOLD, line: 'rgba(245,240,232,0.07)', wm: 'rgba(211,154,33,0.04)',
  btnBorder: 'rgba(201,169,110,0.3)',
};
const LIGHT = {
  // meta/faint/gold darkened from #8b919b/#9aa0a8/#a17d3a, those failed
  // WCAG AA contrast (2.98:1 / 2.48:1 / 3.59:1) against bg #f9f8f4, which
  // needs >=4.5:1 for small text. These clear it at 4.69/4.77/4.72:1.
  bg: '#f9f8f4', ink: '#26282d', body: '#4c515a',
  meta: '#6b7078', faint: '#6a6f75',
  gold: '#8a6a30', line: 'rgba(0,0,0,0.08)', wm: 'rgba(161,125,58,0.05)',
  btnBorder: 'rgba(161,125,58,0.35)',
};

// Rewrites Supabase Storage URLs to use the image-transform API.
// Non-Supabase URLs (external CDNs, static assets) are returned unchanged.
// Supabase transform docs: /storage/v1/render/image/public/{bucket}/{path}
function imgTransform(url, { width, height, quality = 75 } = {}) {
  if (!url) return url;
  const PATTERN = '/storage/v1/object/public/';
  const idx = url.indexOf(PATTERN);
  if (idx < 0) return url; // not a Supabase Storage URL, return as-is
  const base = url.slice(0, idx) + '/storage/v1/render/image/public/' + url.slice(idx + PATTERN.length);
  const params = new URLSearchParams({ quality: String(quality), format: 'webp' });
  if (width)  params.set('width',  String(width));
  if (height) params.set('height', String(height));
  // Strip any existing query string from the original URL before appending new one
  const cleanBase = base.split('?')[0];
  return `${cleanBase}?${params}`;
}

function CategoryLabel({ label, c }) {
  return (
    <span style={{ fontFamily: FU, fontSize: 9, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: c.gold }}>
     {label}
    </span>
  );
}

// ── Hero feature card ──────────────────────────────────────────────────────────
function FeatureCard({ post, onClick, c }) {
  return (
    <article onClick={onClick} style={{ cursor: 'pointer', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ overflow: 'hidden', borderRadius: 2, flex: '0 0 auto', aspectRatio: '16/10' }}>
        <img
          src={imgTransform(post.coverImage, { width: 700, quality: 75 })}
          alt={post.coverImageAlt || post.title}
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: post.coverFocal || 'center', transition: 'transform 0.55s ease', display: 'block' }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.04)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        />
      </div>
      <div style={{ paddingTop: 20, flex: 1 }}>
        <CategoryLabel label={post.categoryLabel} c={c} />
        <h2 style={{ fontFamily: FD, fontSize: 'clamp(24px, 2.8vw, 36px)', fontWeight: 400, color: c.ink, margin: '8px 0 10px', lineHeight: 1.08, letterSpacing: '-0.02em' }}>
          {post.title}
        </h2>
        {post.standfirst && (
          <p style={{ fontFamily: FD, fontSize: 14, fontStyle: 'italic', color: c.body, lineHeight: 1.6, margin: '0 0 14px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {post.standfirst}
          </p>
        )}
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          {post.author?.name && <span style={{ fontFamily: FU, fontSize: 10, color: c.faint }}>{post.author.name}</span>}
          {post.readingTime && <span style={{ fontFamily: FU, fontSize: 10, color: c.meta }}>{post.readingTime} min read</span>}
        </div>
      </div>
    </article>
  );
}

// ── Small secondary card ───────────────────────────────────────────────────────
function SecondaryCard({ post, onClick, last, c }) {
  return (
    <article
      onClick={onClick}
      style={{
        cursor: 'pointer', display: 'flex', gap: 14, alignItems: 'flex-start',
        paddingBottom: last ? 0 : 20,
        borderBottom: last ? 'none' : `1px solid ${c.line}`,
      }}
    >
      <div style={{ width: 126, height: 96, flexShrink: 0, overflow: 'hidden', borderRadius: 2 }}>
        <img
          src={imgTransform(post.coverImage, { width: 252, height: 192, quality: 75 })}
          alt={post.title}
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: post.coverFocal || 'center', transition: 'transform 0.4s ease' }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.07)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <CategoryLabel label={post.categoryLabel} c={c} />
        <h3 style={{ fontFamily: FD, fontSize: 16, fontWeight: 400, color: c.ink, margin: '5px 0 6px', lineHeight: 1.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {post.title}
        </h3>
        {post.readingTime && (
          <span style={{ fontFamily: FU, fontSize: 9, color: c.meta, letterSpacing: '0.06em' }}>{post.readingTime} min read</span>
        )}
      </div>
    </article>
  );
}

// ── Section ────────────────────────────────────────────────────────────────────
export default function MagazineEditorial({ onViewMagazine, onViewMagazineArticle, light = false }) {
  const c = light ? LIGHT : DARK;
  const [posts, setPosts] = useState(null); // null = loading

  useEffect(() => {
    fetchPosts({ published: true })
      .then(({ data }) => {
        if (data?.length) {
          // Total order BEFORE mapping (raw rows carry the date + id keys): re-assert
          // fetchPosts's updated_at-desc+id order client-side, so the feature card +
          // slice(1,4) selection is deterministic regardless of DB delivery order.
          const source = orderBy(data, byDateDesc('updatedAt', 'publishedAt'));
          // Normalise DB shape to match static POSTS shape
          const mapped = source.map(p => ({
            id: p.id,
            slug: p.slug,
            title: p.title,
            standfirst: p.standfirst || p.excerpt || '',
            categoryLabel: p.category_label || p.category || '',
            author: p.author ? { name: typeof p.author === 'object' ? p.author.name : p.author } : null,
            readingTime: p.reading_time || p.readingTime || null,
            featured: p.featured || false,
            coverImage: p.cover_image || p.coverImage || '',
            coverImageAlt: p.cover_image_alt || p.title,
            // Same field the article hero already honours (EditorialArticlePage:3240)
            // and Article Studio already edits, the card was the only surface
            // ignoring it, so a square cover got centre-cropped to 16:10 and lost the
            // subject. fetchPosts runs rows through rowToCamel, so the column arrives
            // as heroFocalPoint; the static POSTS fallback and any raw row use the
            // snake form. Defaults to 'center', so existing cards are unchanged.
            coverFocal: p.heroFocalPoint || p.hero_focal_point || p.coverImageFocal || 'center',
          }));
          // (The `featured` flag is no longer used to pin the feature, it was stranding
          // the section on an old featured post while newer Edit posts published below it.)
          setPosts(mapped);
        } else {
          // Fall back to static editorial content
          const ordered = [...POSTS.filter(p => p.featured), ...POSTS.filter(p => !p.featured)];
          setPosts(ordered);
        }
      })
      .catch(() => {
        const ordered = [...POSTS.filter(p => p.featured), ...POSTS.filter(p => !p.featured)];
        setPosts(ordered);
      });
  }, []);

  if (posts === null) return null; // loading, don't flash empty
  const feature   = posts[0];
  const secondary = posts.slice(1, 4);

  if (!feature) return null;

  return (
    <section style={{ background: c.bg, padding: 'clamp(48px, 6vw, 80px) clamp(20px, 5vw, 72px)', position: 'relative', overflow: 'hidden' }}>
      <style>{`
        .me-grid { display: flex; gap: clamp(28px, 4vw, 56px); align-items: flex-start; flex-wrap: wrap; }
        .me-feature { flex: 2 1 320px; min-width: 0; }
        .me-sidebar { flex: 1 1 240px; min-width: 0; display: flex; flex-direction: column; gap: 20px; }
      `}</style>

      {/* Watermark, bottom right */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: -20,
          right: -10,
          fontFamily: FD,
          fontSize: 'clamp(120px, 12vw, 180px)',
          fontWeight: 400,
          fontStyle: 'italic',
          color: c.wm,
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          userSelect: 'none',
          letterSpacing: '-2px',
          lineHeight: 1,
          zIndex: 0,
        }}
      >
        The LWD Edit
      </span>

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 'clamp(28px, 4vw, 44px)', gap: 16, flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
        <div>
          <div style={{ fontFamily: FU, fontSize: 9, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: c.gold, marginBottom: 6 }}>The Magazine</div>
          <h2 style={{ fontFamily: FD, fontSize: 'clamp(28px, 3.5vw, 44px)', fontWeight: 400, color: c.ink, margin: 0, lineHeight: 1.05, letterSpacing: '-0.02em' }}>From The LWD Edit</h2>
        </div>
        <button
          onClick={onViewMagazine}
          style={{ fontFamily: FU, fontSize: 10, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'none', border: `1px solid ${c.btnBorder}`, color: c.gold, cursor: 'pointer', padding: '9px 18px', borderRadius: 2, transition: 'all 0.2s', flexShrink: 0 }}
          onMouseEnter={e => { e.currentTarget.style.background = `${c.gold}14`; e.currentTarget.style.borderColor = `${c.gold}80`; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = c.btnBorder; }}
        >
          Explore the Magazine →
        </button>
      </div>

      {/* Grid */}
      <div className="me-grid" style={{ position: 'relative', zIndex: 1 }}>
        <div className="me-feature">
          <FeatureCard post={feature} c={c} onClick={() => onViewMagazineArticle?.(feature.slug)} />
        </div>
        <div className="me-sidebar">
          {secondary.map((post, i) => (
            <SecondaryCard key={post.id} post={post} c={c} last={i === secondary.length - 1} onClick={() => onViewMagazineArticle?.(post.slug)} />
          ))}
          {/* Divider + explore link */}
          <div style={{ borderTop: `1px solid ${c.line}`, paddingTop: 20 }}>
            <button
              onClick={onViewMagazine}
              style={{ fontFamily: FD, fontSize: 14, fontStyle: 'italic', background: 'none', border: 'none', color: c.body, cursor: 'pointer', padding: 0, transition: 'color 0.2s', letterSpacing: '0.02em' }}
              onMouseEnter={e => { e.currentTarget.style.color = c.ink; }}
              onMouseLeave={e => { e.currentTarget.style.color = c.body; }}
            >
              Read all articles in the magazine →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
